/**
 * Image Hotspot Element Schema
 * Image with clickable hotspots that reveal content
 */

import type { ElementSchema } from '@ai-content-hub/content-kit';
import type { BlockContent, BlockContentType } from '../block';
import { createDefaultBlockContent } from '../block';
import { registerSchemaI18n } from '../i18n/registerSchemaI18n';

export const imageHotspotElementI18nEn = {
    addHotspot: 'Add hotspot',
    clickHotspot: 'Click hotspot',
    content: 'Content',
    ihsHeading: 'Image & hotspots',
    ihsChangeBackground: 'Change background',
    ihsRemoveBackground: 'Remove background',
    ihsLabelField: 'Label',
    ihsHotspotLabelPlaceholder: 'Hotspot label',
    ihsEditContent: 'Edit content',
    ihsBackgroundAlt: 'Background',
    ihsUploadBackgroundFirst: 'Upload a background image first',
    ihsDialogChangeBgTitle: 'Change background image',
    ihsHotspotContentTitle: 'Hotspot content: {label}',
    ihsPlayerNoImage: 'No image',
    ihsHotspotAriaDefault: 'Hotspot',
    ihsContentRegionAria: 'Content for {label}',
    ihsHotspotFallback: 'Hotspot',
    ihsClose: 'Close',
    ihsResizeHotspotAria: 'Resize',
} as const satisfies Record<string, string>;

export const imageHotspotElementI18nVi = {
    addHotspot: 'Thêm điểm chạm',
    clickHotspot: 'Chọn điểm chạm',
    content: 'Nội dung',
    ihsHeading: 'Ảnh & điểm chạm',
    ihsChangeBackground: 'Đổi ảnh nền',
    ihsRemoveBackground: 'Gỡ ảnh nền',
    ihsLabelField: 'Nhãn',
    ihsHotspotLabelPlaceholder: 'Nhãn điểm chạm',
    ihsEditContent: 'Sửa nội dung',
    ihsBackgroundAlt: 'Ảnh nền',
    ihsUploadBackgroundFirst: 'Tải ảnh nền trước',
    ihsDialogChangeBgTitle: 'Đổi ảnh nền',
    ihsHotspotContentTitle: 'Nội dung điểm chạm: {label}',
    ihsPlayerNoImage: 'Chưa có ảnh',
    ihsHotspotAriaDefault: 'Điểm chạm',
    ihsContentRegionAria: 'Nội dung cho {label}',
    ihsHotspotFallback: 'Điểm chạm',
    ihsClose: 'Đóng',
    ihsResizeHotspotAria: 'Đổi kích thước',
} as const satisfies Record<string, string>;

export type ImageHotspotElementI18nKey = keyof typeof imageHotspotElementI18nEn;

export const defaultImageHotspotElementI18n = {
    en: { ...imageHotspotElementI18nEn },
    vi: { ...imageHotspotElementI18nVi },
};

registerSchemaI18n(defaultImageHotspotElementI18n);

export interface ImageHotspotItem {
    id: string;
    label: string;
    x: number;
    y: number;
    radius: number;
    content: BlockContent;
}

export interface ImageHotspotElementData {
    background: BlockContent;
    hotspots: ImageHotspotItem[];
    allowedContentTypes?: BlockContentType[];
}

export function getBackgroundImageSrc(background: BlockContent | undefined): string {
    if (!background || background.type !== 'image') return '';
    return (background as { src?: string }).src ?? '';
}

export function getBackgroundImageAlt(background: BlockContent | undefined): string {
    if (!background || background.type !== 'image') return '';
    return (background as { alt?: string }).alt ?? '';
}

export function createDefaultHotspot(id?: string): ImageHotspotItem {
    return {
        id: id ?? `hotspot-${Date.now()}`,
        label: '',
        x: 50,
        y: 50,
        radius: 60,
        content: createDefaultBlockContent('text'),
    };
}

export function normalizeImageHotspotData(
    data: Partial<ImageHotspotElementData>
): ImageHotspotElementData {
    const background: BlockContent =
        data.background && typeof data.background === 'object' && 'type' in data.background
            ? data.background
            : createDefaultBlockContent('image');

    const hotspots: ImageHotspotItem[] = (data.hotspots ?? []).map((h, i) => {
        let content: BlockContent;
        if (h.content && typeof h.content === 'object' && 'type' in h.content) {
            content = h.content;
        } else {
            content = createDefaultBlockContent('text');
        }
        return {
            id: h.id ?? `hotspot-${i + 1}`,
            label: h.label ?? '',
            x: typeof h.x === 'number' ? h.x : 50,
            y: typeof h.y === 'number' ? h.y : 50,
            radius: typeof h.radius === 'number' ? Math.max(20, Math.min(150, h.radius)) : 60,
            content,
        };
    });

    return {
        background,
        hotspots,
        allowedContentTypes: data.allowedContentTypes ?? ['text', 'image', 'rich-text'],
    };
}

export const imageHotspotElementSchema: ElementSchema<ImageHotspotElementData> = {
    type: 'element-image-hotspot',
    title: 'Image Hotspot',
    description: 'Image with clickable hotspots that reveal content when clicked.',
    version: '1.0.0',
    category: 'interactive',
    icon: 'MapPin',
    gradable: false,
    tags: ['image', 'hotspot', 'interactive', 'info'],
    fields: [],
    defaultData: {
        background: createDefaultBlockContent('image'),
        hotspots: [],
        allowedContentTypes: ['text', 'image', 'rich-text'],
    },
    i18n: defaultImageHotspotElementI18n,
};
